import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./AgencyAgentEmployeeList.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import Loading from "../../../loading/Loading";
import CloseIcon from "../../../../components/icons/CloseIcon";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import AgencyAgentEmployeeFilter from "../filter/AgencyAgentEmployeeFilter";
import { CommonEnums } from "../../../../enums/common.enums";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import AgencyAgentEmployeeTable from "./AgencyAgentEmployeeTable";
import { RootState } from "../../../../redux/store";
import { commonAction } from "../../../../redux/features/commonSlice";

type IUploadRecord = {
  medium_image_url?: string;
  small_image_url?: string;
  storage_key?: string;
  compressed_file_path?: string;
  original_file_path?: string;
};

type IAgentMini = {
  _id: string;
  email?: string;
  principal_agent_first_name?: string;
  principal_agent_last_name?: string;
  principal_agent_mobile_number?: string;
  profile_image_ref?: IUploadRecord;
};

export interface IAgencyAgentEmployeeData {
  _id: string;
  agency_id?: string;
  agent_id?: IAgentMini | string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  mobile_number?: string;
  mobile_country_code?: string;
  designation?: string;
  status?: string;
  profile_photo?: any;
  createdAt?: string;
  updatedAt?: string;
}

let searchTimer: any;

interface stateType {
  isLoading: boolean;
  totalPage: number;
  pageNumber: number;
  totalDocs: number;
  limit: number;
  listData: IAgencyAgentEmployeeData[];
  search: string;
}

const AgencyAgentEmployeeList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const [state, setState] = useState<stateType>({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    limit: 20,
    listData: [],
    search: "",
  });

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateString;
  };

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const status = searchParams.get(CommonEnums.queryParams?.status) || "";
  const start_date =
    searchParams.get(CommonEnums.queryParams?.start_date) || "";
  const end_date = searchParams.get(CommonEnums.queryParams?.end_date) || "";
  const agent_ids = searchParams.get(CommonEnums.queryParams?.agent_id) || "";
  const agent_names =
    searchParams.get(CommonEnums.queryParams?.agent_name) || "";

  const selectedAgentIds = agent_ids
    ? agent_ids.split(",").filter(Boolean)
    : [];
  const selectedAgentNames = agent_names
    ? agent_names.split(",").filter(Boolean)
    : [];

  const filterValues = {
    status,
    startDate: start_date || "00-00-00",
    endDate: end_date || "00-00-00",
    selectedAgentIds,
    selectedAgentNames,
  };

  const getListDataApi = async (search: string, page: number) => {
    setState({
      ...state,
      isLoading: true,
    });

    let res = await AgencyEmployeeApi.listAgentEmployees({
      page,
      limit: state.limit,
      search,
      status: status ? status : "",
      startDate: start_date ? start_date : "",
      endDate: end_date ? end_date : "",
      agent_ids: agent_ids ? agent_ids : "",
    });

    if (res?.success && res?.data?.success) {
      setState({
        ...state,
        isLoading: false,
        listData: res?.data?.data?.docs || [],
        totalPage: res?.data?.data?.totalPages || 1,
        totalDocs: res?.data?.data?.totalDocs || 0,
        limit: res?.data?.data?.limit || state.limit,
        search,
      });
    } else {
      setState({
        ...state,
        isLoading: false,
        listData: [],
      });
    }
  };

  const onClickPreview = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber - 1,
    });
  };

  const onClickNext = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber + 1,
    });
  };

  const onChangeSearch = (e: any) => {
    let search = e?.target?.value;
    setState({
      ...state,
      search,
      pageNumber: 1,
    });
  };

  const applyFiltersToUrl = (values: any) => {
    const agentIds = values?.selectedAgentIds?.join(",") || "";
    const agentNames = values?.selectedAgentNames?.join(",") || "";

    let navigationString = `agency-agent-employee?${
      CommonEnums.queryParams.status
    }=${values?.status || ""}&${CommonEnums.queryParams.start_date}=${
      values?.startDate === "00-00-00" ? "" : values?.startDate
    }&${CommonEnums.queryParams.end_date}=${
      values?.endDate === "00-00-00" ? "" : values?.endDate
    }&${CommonEnums.queryParams.agent_id}=${encodeURIComponent(
      agentIds,
    )}&${CommonEnums.queryParams.agent_name}=${encodeURIComponent(agentNames)}`;

    navigate(`/${navigationString}`);
    dispatch(commonAction.resetFilter());
  };

  const handleRemoveStatus = () => {
    applyFiltersToUrl({ ...filterValues, status: "" });
  };

  const handleRemoveDate = () => {
    applyFiltersToUrl({
      ...filterValues,
      startDate: "00-00-00",
      endDate: "00-00-00",
    });
  };

  const removeAgentFromFilter = (agentId: string, index: number) => {
    const newIds = filterValues.selectedAgentIds.filter((id) => id !== agentId);
    const newNames = filterValues.selectedAgentNames.filter(
      (_, i) => i !== index,
    );

    applyFiltersToUrl({
      ...filterValues,
      selectedAgentIds: newIds,
      selectedAgentNames: newNames,
    });
  };

  const handleClearFilters = () => {
    navigate("/agency-agent-employee");
    dispatch(commonAction.resetFilter());
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      getListDataApi(state?.search, state?.pageNumber);
    }, 500);
  }, [state?.search, state?.pageNumber]);

  useEffect(() => {
    getListDataApi(state?.search, state?.pageNumber);
  }, [resetFilter, searchParams]);

  const isFilterActive =
    !!filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00") ||
    (filterValues.selectedAgentIds?.length || 0) > 0;

  const startRange = (state.pageNumber - 1) * state.limit + 1;
  const endRange = Math.min(state.pageNumber * state.limit, state.totalDocs);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="agency-employee-list-container">
          <div className="list-heading">
            <p>Agent employees</p>
            <span className="sub-heading">
              A dedicated list of staff and team members supporting your agents
              across the organization
            </span>
          </div>

          <ListTopButtonsAndSearch
            add_btn_name="Add"
            add_btn_link="/agency-agent-employee/create"
            reset_btn_link="/agency-agent-employee"
            placeholder="Search by name or email"
            onChangeSearch={onChangeSearch}
            type={CommonEnums.pageTypes?.agency_agent_employee?.key}
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>

              <div className="chips-wrapper">
                {filterValues.selectedAgentIds.map(
                  (agentId: string, index: number) => (
                    <div className="filter-chip font-inter-400" key={agentId}>
                      <span className="value">
                        Agent:{" "}
                        {filterValues.selectedAgentNames[index] || agentId}
                      </span>
                      <div
                        className="close-btn"
                        onClick={() => removeAgentFromFilter(agentId, index)}
                      >
                        <CloseIcon />
                      </div>
                    </div>
                  ),
                )}

                {filterValues.status && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveStatus}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveDate}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state.isLoading ? (
            <div className="data-not-found">
              <Loading />
            </div>
          ) : state.listData.length === 0 ? (
            <div className="data-not-found">
              <p>List is Empty!</p>
            </div>
          ) : (
            <AgencyAgentEmployeeTable
              listData={state.listData}
              onRefresh={() => getListDataApi(state.search, state.pageNumber)}
            />
          )}

          {!state.isLoading && state.listData.length > 0 && (
            <div className="pagination-wrapper">
              <div className="viewing-text">
                Viewing {state.totalDocs > 0 ? startRange : 0}-{endRange} of{" "}
                {state.totalDocs} results
              </div>

              <div className="custom-pagination">
                <button
                  className="page-btn"
                  disabled={state.pageNumber === 1}
                  onClick={onClickPreview}
                >
                  ‹
                </button>

                <button
                  className="page-btn"
                  disabled={state.pageNumber === state.totalPage}
                  onClick={onClickNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <AgencyAgentEmployeeFilter />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyAgentEmployeeList;
